// filepath: apps/web/src/operator/OperatorRoutes.tsx
//
// FR-UI-OPS-2 AC-1 / AC-5 / AC-8 — the operator `<Routes>` tree.
//
// Mounted INSIDE `<BrowserRouter basename="/operator">` (see
// `OperatorRoot`). All `<Route path>` values are section-relative
// because the router strips the `/operator` basename — so `tenants`
// matches `/operator/tenants`, `effects` matches `/operator/effects`,
// etc. This mirrors the `isOperatorSurface` pathname-prefix invariant
// declaratively (the shell only ever mounts under `/operator/*`).
//
// Route map (AC-by-AC):
//   index (`/`)                     → OperatorSummaryRoute       (AC-2)
//   tenants                         → OperatorTenantsSection     (AC-4)
//   tenants/:tenantId/deprovision   → OperatorDeprovisionRoute   (AC-4/AC-8)
//   effects                         → EffectsMetricsRoute        (AC-5)
//   effects/metrics                 → <Navigate to="/effects">   (AC-5 deep-link)
//   audit                           → OperatorAuditPlaceholder   (AC-6)
//   *                               → <Navigate to="/" replace>  (fallback)
//
// The persistent shell chrome (`OperatorShell`) is the layout route
// element wrapping every section via `<Outlet/>`.

import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { EffectsMetricsRoute } from "@/features/admin/effectsMetrics";
import { OperatorShell } from "./OperatorShell";
import { OperatorSummaryRoute } from "./OperatorSummaryRoute";
import {
  OperatorTenantsSection,
  OperatorDeprovisionRoute,
} from "./OperatorTenantsSection";
import { OperatorAuditPlaceholder } from "./OperatorAuditPlaceholder";

/**
 * OperatorRoutes — the section route tree. Rendered inside the
 * `<BrowserRouter>` established by `OperatorRoot`. The
 * `<OperatorShell>` layout route supplies the persistent left-nav
 * and `<main>` landmark for every child route.
 */
export function OperatorRoutes(): React.ReactElement {
  return (
    <Routes>
      <Route element={<OperatorShell />}>
        {/* AC-2 — Summary is the default landing view. */}
        <Route index element={<OperatorSummaryRoute />} />
        {/* AC-4 — Tenants section (deprovision only in v1). */}
        <Route path="tenants" element={<OperatorTenantsSection />} />
        {/* AC-4 / AC-8 — deprovision sub-flow status (deep-linkable). */}
        <Route
          path="tenants/:tenantId/deprovision"
          element={<OperatorDeprovisionRoute />}
        />
        {/* AC-5 — Effects/Ops section. */}
        <Route path="effects" element={<EffectsMetricsRoute />} />
        {/* AC-5 — deprecated top-level deep-link redirects to /effects. */}
        <Route
          path="effects/metrics"
          element={<Navigate to="/effects" replace />}
        />
        {/* AC-6 — Audit placeholder (zero network calls). */}
        <Route path="audit" element={<OperatorAuditPlaceholder />} />
        {/* Fallback — any unknown path returns to the Summary landing. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
